<template>
  <div class="rtu-view">
    <!-- 页头：设备选择 + 更新时间 -->
    <div class="rtu-topline">
      <el-select v-model="unitId" class="device-select" size="default" @change="onUnitChange">
        <el-option v-for="u in units" :key="u.unitId" :label="u.name" :value="u.unitId">
          <span>{{ u.name }}</span>
          <span v-if="u.dtuName" class="opt-dtu">{{ u.dtuName }}</span>
        </el-option>
      </el-select>
      <span class="updated">{{ t('message.rtu.lastUpdated') }} {{ lastUpdated }} · {{ isStale ? t('message.rtu.stale') : t('message.rtu.live') }}</span>
      <el-button
        class="refresh-btn"
        size="small"
        :icon="Refresh"
        :loading="refreshing"
        :disabled="!unitId || refreshing"
        @click="handleRefresh"
      >{{ refreshing ? t('message.rtu.refreshing') : t('message.rtu.refresh') }}</el-button>
      <el-tag v-if="isDemo" type="warning" size="small" effect="plain">DEMO</el-tag>
    </div>

    <div class="rtu-sections">
      <!-- 数据过期横幅：30 分钟无新数据 -->
      <el-alert v-if="isStale" type="warning" class="stale-banner" :closable="false">
        <template #title>
          <div class="alarm-banner-line">
            <span>{{ t('message.rtu.staleData', { time: lastUpdated }) }}</span>
            <el-button size="small" :icon="Refresh" :loading="refreshing" @click="handleRefresh">
              {{ t('message.rtu.refresh') }}
            </el-button>
          </div>
        </template>
      </el-alert>

      <!-- 报警横幅 -->
      <el-alert v-if="alarms.length && !isStale" type="error" class="alarm-banner" :closable="false">
        <template #title>
          <div class="alarm-banner-line">
            <span>
              <b>{{ t('message.rtu.alarmBanner', { n: alarms.length }) }}</b>
              — {{ alarms.map(a => t(`message.rtu.ind.${a}`)).join(', ') }}
            </span>
            <el-button size="small" type="danger" plain @click="goFdd">{{ t('message.rtu.checkFdd') }} ↓</el-button>
          </div>
        </template>
      </el-alert>

      <!-- 1. 实时状态 -->
      <collapsible-card :title="t('message.rtu.liveSection')" :hint="t('message.rtu.updatesEvery')">
        <div class="tiles">
          <div v-if="showModeTile" class="tile mode-tile">
            <div class="lbl">{{ t('message.rtu.opMode') }}</div>
            <div class="val mode">{{ modeText }}</div>
          </div>
          <div v-for="tile in visibleTiles" :key="tile.key" class="tile">
            <div class="lbl">{{ t(`message.rtu.${tile.key}`) }}</div>
            <div class="val">{{ tileVal(tile) }}<small v-if="!isStale">{{ tile.unit }}</small></div>
          </div>
        </div>
        <div v-if="visibleChips.length" class="chips">
          <el-tag v-for="c in visibleChips" :key="c.key" :type="(!isStale && values[c.var]) ? 'success' : 'info'" effect="plain" round>
            <span class="chip-dot" :class="{ on: !isStale && values[c.var] }" />
            {{ t(`message.rtu.${c.key}`) }} {{ isStale ? '—' : (values[c.var] ? t('message.rtu.on') : t('message.rtu.off')) }}
          </el-tag>
        </div>
      </collapsible-card>

      <!-- 2. 设定值（模板无设定点则整段隐藏） -->
      <collapsible-card v-if="hasSetpoints" :title="t('message.rtu.tabSetpoints')" :hint="t('message.rtu.spHint')">
        <rtu-setpoints-panel v-if="unitId" :key="unitId" :adapter="adapter" :unit-id="unitId" :values="values" />
      </collapsible-card>

      <!-- 3. 健康 / FDD（无固件来源的指示灯隐藏；两列皆空则整卡隐藏） -->
      <collapsible-card v-if="hasFdd" ref="fddCard" :title="t('message.rtu.healthSection')" :hint="t('message.rtu.healthHint')">
        <div class="ind-grid">
          <div v-if="visibleComfort.length">
            <h4 class="ind-h">{{ t('message.rtu.comfortGroup') }}</h4>
            <div class="ind-list">
              <div v-for="[k, v] in visibleComfort" :key="k" class="ind" :class="v">
                <span class="sq" />{{ t(`message.rtu.ind.${k}`) }}
                <span v-if="v === 'alarm'" class="tag">{{ t('message.rtu.alarmTag') }}</span>
              </div>
            </div>
          </div>
          <div v-if="visibleStatus.length">
            <h4 class="ind-h">{{ t('message.rtu.statusGroup') }}</h4>
            <div class="ind-list">
              <div v-for="[k, v] in visibleStatus" :key="k" class="ind" :class="v">
                <span class="sq" />{{ t(`message.rtu.ind.${k}`) }}
                <span v-if="v === 'alarm'" class="tag">{{ t('message.rtu.alarmTag') }}</span>
              </div>
            </div>
          </div>
        </div>
      </collapsible-card>

      <!-- 4. 时间表（模板无时间表寄存器则整段隐藏） -->
      <collapsible-card v-if="hasSchedule" :title="t('message.rtu.schedSection')" :hint="t('message.rtu.schedHint')">
        <rtu-schedule-editor v-if="unitId" :key="unitId" :adapter="adapter" :unit-id="unitId" />
      </collapsible-card>

      <!-- 5. 历史 -->
      <collapsible-card :title="t('message.rtu.historySection')" :hint="t('message.rtu.historyHint')">
        <rtu-history-charts v-if="unitId" :key="unitId" :adapter="adapter" :unit-id="unitId" />
      </collapsible-card>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { ElMessage } from 'element-plus';
import { Refresh } from '@element-plus/icons-vue';
import { getRtuAdapter, isDemoMode } from '@/services/rtuAdapter';
import CollapsibleCard from './CollapsibleCard.vue';
import RtuScheduleEditor from './RtuScheduleEditor.vue';
import RtuSetpointsPanel from './RtuSetpointsPanel.vue';
import RtuHistoryCharts from './RtuHistoryCharts.vue';
import alarmMap from '@/config/rtuAlarmMap.json';

const STALE_MS = 30 * 60 * 1000; // 超过30分钟无新数据视为过期

const { t } = useI18n();
const route = useRoute();
const adapter = getRtuAdapter();
const isDemo = isDemoMode();

const units = ref([]);
const unitId = ref('');
const values = reactive({});
const healthState = reactive({ comfort: {}, status: {} });
const lastUpdated = ref('—');
const refreshing = ref(false);
const fddCard = ref(null);
// 该机组模板实际映射的逻辑点集合（null=尚未加载，加载前不隐藏，避免闪烁）
const mappedVars = ref(null);
const dataTs = ref(null);   // 最近数据的时间戳（服务端“钟面时间”解析为本地 Date）
const nowTick = ref(Date.now());
// 服务端当前时间基准：服务端与客户端可能相差多个时区，必须以服务端时钟判断过期。
// 记录一次服务端时间及取回时的本地时刻，之后按本地流逝推进。
const serverEpoch = ref(null);   // getServerTime() 的毫秒值（同为“钟面时间”解析）
const serverFetchedAt = ref(0);
let staleTimer = null;

// 服务端“当前时间”毫秒：基准 + 本地流逝；未取到时退回本地时钟。
const serverNowMs = () => (serverEpoch.value != null
  ? serverEpoch.value + (nowTick.value - serverFetchedAt.value)
  : nowTick.value);

// 模板未包含的点位一律隐藏（无固件来源）。未加载完成前默认显示。
const isMapped = (name) => !mappedVars.value || mappedVars.value.has(name);
// 数据过期：服务端时间与数据时间戳相差超过 STALE_MS（阈值宽松，因服务端仅在值变化时更新时间戳）
const isStale = computed(() => !!dataTs.value && (serverNowMs() - dataTs.value.getTime() > STALE_MS));

const showModeTile = computed(() => isMapped('op_mode'));
const visibleTiles = computed(() => tiles.filter((tl) => isMapped(tl.var)));
const visibleChips = computed(() => stateChips.filter((c) => isMapped(c.var)));
// FDD 指示灯：其任一来源变量在模板中即显示（comfort 的 st_* 无寄存器 → 隐藏）
const indHasData = (group, key) => (alarmMap[group]?.[key]?.vars || []).some((v) => isMapped(v));
const visibleComfort = computed(() =>
  Object.entries(healthState.comfort).filter(([k]) => indHasData('comfort', k)));
const visibleStatus = computed(() =>
  Object.entries(healthState.status).filter(([k]) => indHasData('status', k)));
const hasFdd = computed(() => visibleComfort.value.length || visibleStatus.value.length);
const hasSetpoints = computed(() => ['sp_occ_cool', 'sp_occ_heat', 'sp_unocc_cool', 'sp_unocc_heat'].some(isMapped));
const hasSchedule = computed(() => ['sched_occs_mon', 'sched_smart_mon'].some(isMapped));
// 过期时点位值以“—”呈现，避免把陈旧的 0 当作实时读数
const tileVal = (tl) => (isStale.value ? '—' : fmt(values[tl.var], tl.dp));

const tiles = [
  { key: 'roomTemp', var: 'room_temp', unit: '°F', dp: 1 },
  { key: 'oat', var: 'oat', unit: '°F', dp: 1 },
  { key: 'sat', var: 'sat', unit: '°F', dp: 1 },
  { key: 'fanSpeed', var: 'fan_hz', unit: 'Hz', dp: 2 },
  { key: 'compSpeed', var: 'comp_hz', unit: 'Hz', dp: 1 },
  { key: 'damper', var: 'damper', unit: '%', dp: 1 },
  { key: 'power', var: 'power_kw', unit: 'kW', dp: 2 },
];
const stateChips = [
  { key: 'indoorFanChip', var: 'fan_on' },
  { key: 'y1', var: 'y1' },
  { key: 'y2', var: 'y2' },
  { key: 'w1', var: 'w1' },
  { key: 'w2', var: 'w2' },
  { key: 'occupiedChip', var: 'occupied' },
];

const alarms = computed(() =>
  Object.entries(healthState.status).filter(([, v]) => v === 'alarm').map(([k]) => k)
);

const modeText = computed(() => {
  if (isStale.value) return '—';
  const m = values.op_mode;
  return m === undefined || m === null ? '—' : t(`message.rtu.modes.${Number(m)}`);
});

const fmt = (v, dp) => (v == null ? '—' : Number(v).toFixed(dp));

function applySnapshot(snap) {
  Object.assign(values, snap.values);
  Object.assign(healthState.comfort, snap.health.comfort);
  Object.assign(healthState.status, snap.health.status);
  lastUpdated.value = snap.ts.toLocaleTimeString();
  dataTs.value = snap.ts;
  nowTick.value = Date.now();
}

function goFdd() {
  fddCard.value?.expand();
  fddCard.value?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

let unsubscribe = null;

async function attach(id) {
  if (unsubscribe) { unsubscribe(); unsubscribe = null; }
  // 清空旧机组残留值（避免无该变量的机组沿用上台机器的数值）
  Object.keys(values).forEach((k) => { delete values[k]; });
  dataTs.value = null;
  mappedVars.value = null; // 切换机组时先不隐藏，待模板加载
  // 模板映射：决定哪些点位有固件来源（无来源的一律隐藏）
  adapter.getMappedVars(id).then((s) => { mappedVars.value = s; }).catch(() => { mappedVars.value = null; });
  try {
    applySnapshot(await adapter.getRealtime(id));
  } catch (e) {
    console.error('[rtu] realtime load failed:', e);
    ElMessage.error(t('message.rtu.loadFailed'));
    return;
  }
  unsubscribe = adapter.subscribe(id, applySnapshot);
}

// 服务端时间基准（用于过期判断）；取一次即可，之后按本地流逝推进。
async function loadServerTime() {
  const d = await adapter.getServerTime().catch(() => null);
  if (d) { serverEpoch.value = d.getTime(); serverFetchedAt.value = Date.now(); }
}

// 主动刷新：下发点名强制网关立即回读上报（被动订阅上报稀疏时按需刷新）
async function handleRefresh() {
  if (!unitId.value || refreshing.value) return;
  refreshing.value = true;
  try {
    loadServerTime(); // 顺带校准服务端时间基准
    applySnapshot(await adapter.refresh(unitId.value));
    ElMessage.success(t('message.rtu.refreshed'));
  } catch (e) {
    console.error('[rtu] refresh failed:', e);
    ElMessage.error(t('message.rtu.refreshFailed'));
  } finally {
    refreshing.value = false;
  }
}

function onUnitChange(id) {
  attach(id);
}

onMounted(async () => {
  loadServerTime();
  try {
    units.value = await adapter.getUnits();
  } catch (e) {
    console.error('[rtu] unit list failed:', e);
    ElMessage.error(t('message.rtu.loadFailed'));
    return;
  }
  // 概览页/侧栏跳转可带 ?unit=（旧链接 ?deviceCode= 仍兼容）
  const wanted = route.query.unit || route.query.deviceCode;
  if (wanted && units.value.some((u) => u.unitId === wanted)) {
    unitId.value = wanted;
  } else if (units.value.length) {
    unitId.value = units.value[0].unitId;
  }
  if (unitId.value) await attach(unitId.value);
});
// 侧栏 RTU 链接在本页内切换机组时只改 query —— 跟随切换
watch(() => route.query.unit || route.query.deviceCode, (id) => {
  if (id && id !== unitId.value && units.value.some((u) => u.unitId === id)) {
    unitId.value = id;
    attach(id);
  }
});

// 每分钟推进时钟以重新评估过期状态（离线时无新快照驱动）
onMounted(() => { staleTimer = setInterval(() => { nowTick.value = Date.now(); }, 60000); });
onBeforeUnmount(() => { if (unsubscribe) unsubscribe(); if (staleTimer) clearInterval(staleTimer); });
</script>

<style scoped>
.rtu-view { padding: 4px 4px 32px; max-width: 1180px; }
.rtu-topline { display: flex; align-items: center; gap: 12px; padding: 4px 0 10px; }
.device-select { width: 260px; }
.opt-dtu { float: right; font-size: 11px; color: var(--el-text-color-placeholder); }
.updated { font-size: 12px; color: var(--el-text-color-secondary); }

.rtu-sections { display: flex; flex-direction: column; gap: 14px; }

.alarm-banner-line { display: flex; align-items: center; gap: 12px; width: 100%; }
.alarm-banner-line > span { flex: 1; }

.tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(128px, 1fr)); gap: 10px; }
.tile { background: var(--el-fill-color-light); border: 1px solid var(--el-border-color-lighter); border-radius: 6px; padding: 10px 12px; }
.tile .lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--el-text-color-secondary); }
.tile .val { font-size: 22px; font-weight: 600; font-variant-numeric: tabular-nums; margin-top: 2px; }
.tile .val small { font-size: 12px; font-weight: 400; color: var(--el-text-color-secondary); margin-left: 2px; }
.mode-tile { border-color: var(--el-color-primary-light-5); }
.tile .val.mode { font-size: 16px; line-height: 1.9; }

.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.chip-dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: var(--el-color-info-light-5); margin-right: 4px; }
.chip-dot.on { background: var(--el-color-success); }

.ind-grid { display: grid; grid-template-columns: 1fr 1.4fr; gap: 16px; }
@media (max-width: 900px) { .ind-grid { grid-template-columns: 1fr; } }
.ind-h { margin: 0 0 8px; font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--el-text-color-secondary); }
.ind-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; }
.ind {
  display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--el-text-color-regular);
  border: 1px solid var(--el-border-color-lighter); border-radius: 4px; padding: 7px 10px;
  background: var(--el-fill-color-light);
}
.ind .sq { width: 12px; height: 12px; border-radius: 2px; flex: none; background: var(--el-color-info-light-5); }
.ind.ok .sq { background: var(--el-color-success); }
.ind.alarm { border-color: var(--el-color-danger); color: var(--el-text-color-primary); font-weight: 600; }
.ind.alarm .sq { background: var(--el-color-danger); }
.ind .tag { margin-left: auto; font-size: 10.5px; color: var(--el-color-danger); font-weight: 700; letter-spacing: 0.04em; }
</style>
